/**
 * @fileoverview Service for Baselinker account currency settings.
 * @module features/settings/api/baselinkerCurrencyService
 */

import apiClient from '@/lib/apiClient'
import { z } from 'zod'

/** ISO-4217 currency code (3 uppercase letters). */
export const CurrencyCodeSchema = z
  .string()
  .length(3, 'Currency code must be 3 letters')
  .regex(/^[A-Z]{3}$/)

/** Response schema for GET /api/baselinker/settings/currency */
export const BaselinkerCurrencyResponseSchema = z.object({
  currency: CurrencyCodeSchema.nullable(),
})

export type BaselinkerCurrencyResponse = z.infer<typeof BaselinkerCurrencyResponseSchema>

/** Request schema for PUT /api/baselinker/settings/currency */
export const SetBaselinkerCurrencyRequestSchema = z.object({
  currency: CurrencyCodeSchema,
})

export type SetBaselinkerCurrencyRequest = z.infer<typeof SetBaselinkerCurrencyRequestSchema>

/**
 * Fetch current Baselinker account currency setting.
 * @returns The configured currency or null if not set
 */
export async function getBaselinkerCurrency(): Promise<BaselinkerCurrencyResponse> {
  const res = await apiClient.get('/baselinker/settings/currency')
  return BaselinkerCurrencyResponseSchema.parse(res.data)
}

/**
 * Set Baselinker account currency.
 * @param currency ISO-4217 currency code
 */
export async function setBaselinkerCurrency(currency: string): Promise<void> {
  const safe = SetBaselinkerCurrencyRequestSchema.parse({ currency })
  await apiClient.put('/baselinker/settings/currency', safe)
}
